import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClock,
  FiCheckCircle,
  FiLoader,
  FiAlertCircle,
  FiPlus,
  FiChevronRight,
  FiCalendar
} from 'react-icons/fi';
import { fetchTickets } from '../api/api';
import './UserDashboard.css';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTickets()
      .then((data) => { if (!cancelled) setTickets(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setTickets([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Analytics
  const analytics = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter(t => t.status === 'open' || t.status === 'pending').length;
    const inProgress = tickets.filter(t => t.status === 'in-progress' || t.status === 'assigned').length;
    const resolved = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    const highPriority = tickets.filter(t => t.priority === 'high' || t.priority === 'urgent').length;

    // Last 7 days tickets
    const lastWeek = tickets.filter(t => {
      const created = new Date(t.created_at || t.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return created >= weekAgo;
    }).length;

    return { total, open, inProgress, resolved, highPriority, lastWeek };
  }, [tickets]);

  // Recent tickets (last 5)
  const recentTickets = useMemo(() => {
    return [...tickets]
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
      .slice(0, 5);
  }, [tickets]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
      case 'pending': return <FiClock className="status-icon" />;
      case 'in-progress':
      case 'assigned': return <FiLoader className="status-icon spinning" />;
      case 'resolved':
      case 'closed': return <FiCheckCircle className="status-icon" />;
      default: return <FiAlertCircle className="status-icon" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open':
      case 'pending': return 'status-open';
      case 'in-progress':
      case 'assigned': return 'status-progress';
      case 'resolved':
      case 'closed': return 'status-resolved';
      default: return 'status-open';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="user-dashboard-enhanced">
      {/* Header */}
      <div className="dashboard-header-compact">
        <div className="header-info">
          <h1>My Dashboard</h1>
          <p>Track your complaints and service requests</p>
        </div>
        <button className="new-ticket-btn" onClick={() => navigate('/user/new-ticket')}>
          <FiPlus /> New Complaint
        </button>
      </div>

      {/* Stats Grid - Compact */}
      <div className="stats-grid-compact">
        <div className="stat-mini">
          <div className="stat-mini-icon" style={{ background: 'rgba(245, 124, 0, 0.1)' }}>
            <FiAlertCircle style={{ color: 'var(--primary)' }} />
          </div>
          <div className="stat-mini-content">
            <span className="stat-mini-value">{loading ? '—' : analytics.total}</span>
            <span className="stat-mini-label">Total</span>
          </div>
        </div>

        <div className="stat-mini">
          <div className="stat-mini-icon" style={{ background: 'rgba(255, 152, 0, 0.1)' }}>
            <FiClock style={{ color: '#FF9800' }} />
          </div>
          <div className="stat-mini-content">
            <span className="stat-mini-value">{loading ? '—' : analytics.open}</span>
            <span className="stat-mini-label">Pending</span>
          </div>
        </div>

        <div className="stat-mini">
          <div className="stat-mini-icon" style={{ background: 'rgba(33, 150, 243, 0.1)' }}>
            <FiLoader style={{ color: '#2196F3' }} />
          </div>
          <div className="stat-mini-content">
            <span className="stat-mini-value">{loading ? '—' : analytics.inProgress}</span>
            <span className="stat-mini-label">In Progress</span>
          </div>
        </div>

        <div className="stat-mini">
          <div className="stat-mini-icon" style={{ background: 'rgba(76, 175, 80, 0.1)' }}>
            <FiCheckCircle style={{ color: '#4CAF50' }} />
          </div>
          <div className="stat-mini-content">
            <span className="stat-mini-value">{loading ? '—' : analytics.resolved}</span>
            <span className="stat-mini-label">Resolved</span>
          </div>
        </div>
      </div>

      {/* Analytics Summary Bar */}
      <div className="analytics-bar">
        <div className="analytics-item">
          <FiCalendar className="analytics-icon" />
          <span className="analytics-text">
            <strong>{analytics.lastWeek}</strong> tickets this week
          </span>
        </div>
        {analytics.highPriority > 0 && (
          <div className="analytics-item urgent">
            <FiAlertCircle className="analytics-icon" />
            <span className="analytics-text">
              <strong>{analytics.highPriority}</strong> high priority
            </span>
          </div>
        )}
      </div>

      {/* Recent Tickets Section */}
      <section className="recent-tickets-section">
        <div className="section-header-compact">
          <h2>Recent Tickets</h2>
          {tickets.length > 5 && (
            <button className="view-all-btn" onClick={() => navigate('/user/history')}>
              View All <FiChevronRight />
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-mini">
            <div className="spinner-mini" />
            <span>Loading...</span>
          </div>
        ) : recentTickets.length > 0 ? (
          <div className="tickets-list-compact">
            {recentTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="ticket-item-compact"
                onClick={() => navigate(`/user/tickets/${ticket.id}`)}
              >
                <div className={`ticket-status-badge ${getStatusClass(ticket.status)}`}>
                  {getStatusIcon(ticket.status)}
                </div>
                <div className="ticket-info-mini">
                  <h4 className="ticket-title-mini">{ticket.title}</h4>
                  <p className="ticket-meta-mini">
                    #{ticket.ticket_number || ticket.id} • {formatDate(ticket.created_at || ticket.createdAt)}
                  </p>
                </div>
                <div className="ticket-priority-mini">
                  <span className={`priority-badge-mini priority-${ticket.priority || 'medium'}`}>
                    {ticket.priority || 'medium'}
                  </span>
                </div>
                <FiChevronRight className="ticket-arrow" />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state-mini">
            <div className="empty-icon-mini">
              <FiCheckCircle />
            </div>
            <p>No tickets yet</p>
            <button className="create-ticket-btn-mini" onClick={() => navigate('/user/new-ticket')}>
              <FiPlus /> Create your first complaint
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default UserDashboard;
